import { apiRequest } from "../client";
import type {
  CompanyAsset,
  CompanyAssetCreate,
  CompanyAssetGroup,
  CompanyAssetGroupCreate,
  CompanyAssetGroupUpdate,
  CompanyAssetUpdate,
  DynamicBindingRequest,
  RecentImageUsage,
  ResolvedBinding,
  ResolvedStaffField,
  ResolvedStaffMember,
  StaffFieldBindingRequest,
  StaffResolveRequest,
} from "./types";

export async function listAssetGroups(): Promise<CompanyAssetGroup[]> {
  const response = await apiRequest<{ groups: CompanyAssetGroup[] }>({
    method: "GET",
    url: "/company-assets/groups",
  });
  return (response.groups || []).map(normalizeCompanyAssetGroup);
}

export async function updateAssetGroup(
  groupId: string,
  body: CompanyAssetGroupUpdate
): Promise<CompanyAssetGroup> {
  const group = await apiRequest<CompanyAssetGroup & { id?: string }>({
    method: "PATCH",
    url: `/company-assets/groups/${groupId}`,
    data: body,
  });
  return normalizeCompanyAssetGroup(group);
}

export async function createAssetGroup(
  body: CompanyAssetGroupCreate
): Promise<CompanyAssetGroup> {
  const group = await apiRequest<CompanyAssetGroup & { id?: string }>({
    method: "POST",
    url: "/company-assets/groups",
    data: body,
  });
  return normalizeCompanyAssetGroup(group);
}

function normalizeCompanyAssetGroup(
  group: CompanyAssetGroup & { id?: string }
): CompanyAssetGroup {
  return {
    ...group,
    _id: group._id || group.id || "",
  };
}

function normalizeCompanyAsset(
  asset: CompanyAsset & { id?: string }
): CompanyAsset {
  return {
    ...asset,
    _id: asset._id || asset.id || "",
  };
}

export async function listCompanyAssets(
  groupId?: string
): Promise<CompanyAsset[]> {
  const response = await apiRequest<{ assets: CompanyAsset[] }>({
    method: "GET",
    url: "/company-assets",
    params: groupId ? { group_id: groupId } : undefined,
  });
  return (response.assets || []).map(normalizeCompanyAsset);
}

export async function createCompanyAsset(
  body: CompanyAssetCreate
): Promise<CompanyAsset> {
  const asset = await apiRequest<CompanyAsset & { id?: string }>({
    method: "POST",
    url: "/company-assets",
    data: body,
  });
  return normalizeCompanyAsset(asset);
}

export async function updateCompanyAsset(
  assetId: string,
  body: CompanyAssetUpdate
): Promise<CompanyAsset> {
  const asset = await apiRequest<CompanyAsset & { id?: string }>({
    method: "PATCH",
    url: `/company-assets/${assetId}`,
    data: body,
  });
  return normalizeCompanyAsset(asset);
}

export async function deleteCompanyAsset(assetId: string): Promise<void> {
  await apiRequest({
    method: "DELETE",
    url: `/company-assets/${assetId}`,
  });
}

export async function listRecentImages(): Promise<RecentImageUsage[]> {
  const response = await apiRequest<{ images: RecentImageUsage[] }>({
    method: "GET",
    url: "/company-assets/recent",
  });
  return response.images;
}

export async function logRecentImageUsage(
  imageIds: string[],
  reportId?: string
): Promise<void> {
  await apiRequest({
    method: "POST",
    url: "/company-assets/recent/log",
    data: {
      image_ids: imageIds,
      report_id: reportId,
    },
  });
}

export async function resolveImageBindings(
  bindings: DynamicBindingRequest[],
  clientId?: string
): Promise<ResolvedBinding[]> {
  const response = await apiRequest<{ resolved: ResolvedBinding[] }>({
    method: "POST",
    url: "/company-assets/resolve",
    data: { bindings, client_id: clientId },
  });
  return response.resolved;
}

export async function resolveStaff(
  staff: StaffResolveRequest[],
  fields?: string[]
): Promise<ResolvedStaffMember[]> {
  const response = await apiRequest<{ resolved: ResolvedStaffMember[] }>({
    method: "POST",
    url: "/company-assets/resolve-staff",
    data: { staff, fields },
  });
  return response.resolved;
}

export async function resolveStaffFieldBindings(
  bindings: StaffFieldBindingRequest[],
  clientId: string
): Promise<ResolvedStaffField[]> {
  const response = await apiRequest<{ resolved: ResolvedStaffField[] }>({
    method: "POST",
    url: "/company-assets/resolve-staff-fields",
    data: { bindings, client_id: clientId },
  });
  return response.resolved;
}

export type {
  AssetFieldDefinition,
  CompanyAsset,
  CompanyAssetCreate,
  CompanyAssetGroup,
  CompanyAssetGroupCreate,
  CompanyAssetGroupUpdate,
  CompanyAssetUpdate,
  DynamicBindingRequest,
  ImageConfig,
  ImageDynamicBinding,
  RecentImageUsage,
  ResolvedBinding,
  ResolvedStaffField,
  ResolvedStaffMember,
  RolePool,
  StaffFieldBindingRequest,
  StaffResolveRequest,
} from "./types";
